import { TFunction } from 'i18next';
import { Study } from '../types/member';

export type StudyLike = Study & {
  institution?: string;
  institutionEn?: string;
  institutionGr?: string;
};

/** Shared study chronology validation used by registration and profile editing. */
export function validateStudiesList(
  studies: StudyLike[] | undefined | null,
  t: TFunction,
  options?: { requireAtLeastOne?: boolean }
): Record<string, string> {
  const errors: Record<string, string> = {};
  const currentYear = new Date().getFullYear();
  const list = studies ?? [];

  if (options?.requireAtLeastOne && !list.length) {
    errors.studies = t('register.studiesRequired');
    return errors;
  }

  let hasUniversity = false;
  list.forEach(study => {
    const inst =
      study.institution?.trim() ||
      study.institutionEn?.trim() ||
      study.institutionGr?.trim();
    if (inst) hasUniversity = true;

    const start = study.startYear;
    const end = study.completionYear;
    const duration = study.duration;
    const id = study.id || 'unknown';

    if (start != null && (start < 1900 || start > currentYear + 1)) {
      errors[`studies.${id}.startYear`] = t('register.studies.startYearInvalidDetail', {
        year: start,
        min: 1900,
        max: currentYear + 1,
        defaultValue: `Start year ${start} is outside the allowed range (${1900}–${currentYear + 1}).`,
      });
    }

    if (study.isCurrent) {
      if (duration == null || Number.isNaN(duration) || duration < 1 || duration > 20) {
        errors[`studies.${id}.duration`] = t('register.studies.durationInvalidDetail', {
          duration: duration ?? '—',
          defaultValue: `Duration must be between 1 and 20 years (got ${duration ?? 'empty'}).`,
        });
      }
    } else if (end != null) {
      // Disallow far-future completion; also reject completion before start.
      if (end < 1900 || end > currentYear + 1) {
        errors[`studies.${id}.completionYear`] = t('register.studies.completionYearInvalidDetail', {
          year: end,
          min: 1900,
          max: currentYear + 1,
          defaultValue: `Completion year ${end} is outside the allowed range (${1900}–${currentYear + 1}).`,
        });
      }
      if (start != null && end < start) {
        errors[`studies.${id}.completionYear`] = t('register.studies.yearsOutOfOrderDetail', {
          start,
          end,
          defaultValue: `Completion year (${end}) cannot be before start year (${start}).`,
        });
      }
    } else if (start != null && !study.isCurrent) {
      // Started but neither current nor completion year — allow empty completion
      // only when intentionally left blank; chronology is checked when both present.
    }
  });

  if (options?.requireAtLeastOne && !hasUniversity) {
    errors.studies = t('register.studiesRequired');
  }

  return errors;
}
